/**
 * Agent Filters Component
 * 
 * Modern, compact filtering component for agents list
 * with inline search and better UX
 */

import { FC, useCallback, useState, useEffect } from 'react';
import { MdSearch, MdClear } from 'react-icons/md';
import type { AgentFilters as AgentFiltersType } from '@/types';
import styles from './AgentFilters.module.css';

export interface AgentFiltersProps {
  filters: AgentFiltersType;
  onFiltersChange: (filters: AgentFiltersType) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
}

const AgentFilters: FC<AgentFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  isLoading = false,
}) => {
  const [searchValue, setSearchValue] = useState(filters.hostname || '');

  /**
   * Debounced search - aplica filtro após 300ms sem digitação
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onFiltersChange({
        ...filters,
        hostname: searchValue || undefined,
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchValue]); // Removido onFiltersChange das dependências

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  /**
   * Handle clear search
   */
  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    onClearFilters();
  }, [onClearFilters]);

  /**
   * Handle Enter key press
   */
  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onFiltersChange({
        ...filters,
        hostname: searchValue || undefined,
      });
    }
  }, [searchValue, filters, onFiltersChange]);

  const hasActiveFilter = searchValue.trim() !== '';

  return (
    <div className={styles.searchContainer}>
      <div className={styles.searchWrapper}>
        <div className={styles.searchInputGroup}>
          <MdSearch className={styles.searchIcon} size={20} />
          <input
            type="text"
            placeholder="Buscar por hostname..."
            value={searchValue}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            className={styles.searchInput}
            aria-label="Buscar agentes por hostname"
          />
          {hasActiveFilter && (
            <button
              type="button"
              onClick={handleClearSearch}
              disabled={isLoading}
              className={styles.clearButton}
              aria-label="Limpar busca"
            >
              <MdClear size={18} />
            </button>
          )}
        </div>
        
        {isLoading && (
          <div className={styles.loadingIndicator}>
            <div className={styles.spinner}></div>
          </div>
        )}
      </div>
      
      {hasActiveFilter && (
        <div className={styles.filterInfo}>
          Filtrando por hostname: <strong>"{searchValue}"</strong>
        </div>
      )}
    </div>
  );
};

export default AgentFilters;